<template>
    <el-dialog :title="dialogConfig.title"
        :visible="showDialog"
        append-to-body
        width="400px"
        class="message-dialog"
        :before-close="handleClose">
        <div class="message-wrapper">
            <template v-if="!notifiedPatient">
                <div class="top-send">
                    Send to
                </div>
                <div class="input-field">
                    <el-checkbox v-model="sendEmail">Email</el-checkbox>
                    <div class="input-text">
                        <el-input size="small" placeholder="johndoe@gmail.com" v-model="email" :disabled="!sendEmail"></el-input>
                        <span :class="['input-error', { show : sendEmail && triedOnceEmailFlag && invalidEmail}]">
                            Please enter a valid email address
                        </span>
                    </div>
                </div>
                <div class="input-field">
                    <el-checkbox v-model="sendSms">SMS</el-checkbox>
                    <div class="input-text">
                        <el-input size="small" placeholder="(123) 456-7890" v-model="phone" :disabled="!sendSms"></el-input>
                        <span :class="['input-error', { show : sendSms && triedOncePhoneFlag && invalidPhone}]">
                            Please enter a valid mobile number
                        </span>
                    </div>
                </div>
                <div class="message-content">
                    {{  dialogConfig.content }}
                </div>
                <div class="submit-button">
                    <el-button @click="handleClose" type="text" class="cancel-button" >Cancel</el-button>
                    <LoadingButton 
                        type="primary"
                        @click="sendMessage"
                        :loading="isNotifyingPatient"
                        >
                        Send
                    </LoadingButton>
                </div>
            </template>
            <template v-else>
                <div class="top-send">
                    <CheckIcon></CheckIcon>&nbsp;
                    {{ dialogConfig.successSubtitle }} {{  successByMethods }}
                </div>
                <div class="message-success">
                    {{ dialogConfig.successContent }}
                </div>
                <div class="submit-button">
                    <el-button type="primary" @click="handleClose">
                        Done
                    </el-button>
                </div>
            </template>
        </div>
    </el-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

import validEmail from '@/module/util/valid-email'
import validPhone from '@/module/util/valid-phone'


import LoadingButton from '@/module/component/LoadingButton'
import CheckIcon from './icon/Check'
/**
 * Dialog component for sending
 * messages to patients for downloading apps,
 * allowing notifications, etc
 * @component
 */
export default {
    name: 'call-dialog',

    watch: {
        /**
         * Resetting the flag that signifies if the send button has been clicked or not.
         */
        sendSms: function() {
            if (!this.sendSms) {
                this.triedOncePhoneFlag = false
            }
        },
        /**
         * Resetting the flag for email that signifies if the send button has been clicked or not.
         */
        sendEmail: function() {
            if (!this.sendEmail) {
                this.triedOnceEmailFlag = false
            }
        }
    },

    data() {
        return {
            /**
             * Email for sending email
             */
            email: this.patient.email ? this.patient.email : '',
            /**
             * Phone number to send sms
             */
            phone: this.patient.phone ? this.patient.phone : '',
            /**
             * Status of whether email needs to be sent
             */
            sendEmail: this.patient.email ? true : false,
            /**
             * Status of whether sms needs to be sent
             */
            sendSms: this.patient.phone ? true : false,
            /**
             * Status whether the patient has been notified succesfully
             */
            notifiedPatient: false,
            /**
             * Flag that signifies if reminder has been tried to be sent once via SMS
             * Used for showing error only after send has been clicked at least once.
             */
            triedOncePhoneFlag: false,
            /**
             * Flag that signifies if reminder has been tried to be sent once via Email
             * Used for showing error only after send has been clicked at least once.
             */
            triedOnceEmailFlag: false
        }
    },

    props: {
        /**
         * Main patient object
         */
        patient: {
            type: Object,
            required: true
        },
        providerId: {
            type: String,
            required: true
        },
        /**
         * Type of dialog to be sent 
         * (i.e Asking for permissions or reminding to donwload app)
         * @values permission, reminder
         */
        dialogType: {
            type: String,
            required: true,
            default: 'permission'
        },
        /**
         * Whether to show dialog box or not
         */
        showDialog: {
            type: Boolean,
            default: false
        }
    },
    

    methods: {
        ...mapActions('provider', [
            'notifyPatient',
        ]),
        /**
         * Closes the dialog box 
         */
        handleClose() {
            // Setting notified patient as default (false) before closing
            // but inside a `setTimeout` so that it doesn't appear before 
            // the dialog box is closed
            setTimeout(() => {
                // setting the default values of the flags
                this.triedOncePhoneFlag = false
                this.triedOnceEmailFlag = false
                this.notifiedPatient = false
            }, 500)
            /**
             * Close event for dialog
             */
            this.$emit('close')
        },
        /**
         * Send the message
         */
        sendMessage() {
            // Turning the flag to true as send button has been clicked.
            this.triedOncePhoneFlag = true
            this.triedOnceEmailFlag = true

            if (this.isInvalidToSend)
                return
            let notifyObject = {
                type: this.dialogConfig.type,
                patient: this.patient.id,
                priority: true
            }
            if(this.providerId.length > 0)
                notifyObject.provider = this.providerId
            if(this.sendEmail)
                notifyObject.email = this.email
            if(this.sendSms)
                notifyObject.phone = this.phone
            this.notifyPatient(notifyObject).then(() => this.notifiedPatient = true)
        }
    },

    computed: {
        ...mapState('provider', {
            isNotifyingPatient: state => state.notifyingPatient
        }),
        ...mapGetters('provider', [
            'getProviderNameById'
        ]),
        providerName() {
            return this.getProviderNameById(this.providerId)
        },
        /**
         * Calculates the content to be shown
         * on the dialog box depending on `dialogType`
         */
        dialogConfig() {
            const dialogObj = {
                'permission': {
                    type: 'camera-permission',
                    title: 'Ask For Video Permissions',
                    content: 'In order to receive video call from your doctor, you must Allow Notifications and Camera access in DoctorPlan app. Please click the button below on your mobile device to open the DoctorPlan app. We will guide you to allow access in the app.',
                    successSubtitle: 'Permission request sent by ',
                    successContent: 'The video icon next to the patient name will turn green when permissions are granted.'
                },
                'reminder': {
                    type: 'camera-download',
                    title: 'Send Reminder to Download App',
                    content: `In order to receive a video call from ${this.providerName}, you must download/update the DoctorPlan App and allow notifications when prompted. Please click on the link below to download the app.`,
                    successSubtitle: 'Reminder sent by ',
                    successContent: 'The video icon next to the patient name will turn grey / green when the patient has downloaded the app.'
                }
            }
            return dialogObj[this.dialogType]
        },
        successByMethods() {
            let successMethods = ''
            if(this.sendEmail)
                successMethods += 'Email'
            if(this.sendEmail && this.sendSms)
                successMethods += ' and '
            if(this.sendSms)
                successMethods += 'SMS'
            return successMethods
        },
        /**
         * Check if phone number is invalid
         */
        invalidPhone() {
            return this.sendSms && !validPhone(this.phone)
        },
        /**
         * Checks if email is invalid
         */
        invalidEmail() {
            return this.sendEmail && !validEmail(this.email)
        },
        /**
         * Checks if message is invalid and cannot be sent
         */
        isInvalidToSend() {
            if(!this.sendSms && !this.sendEmail)
                return true
            return this.invalidEmail || this.invalidPhone
        }
    },

    components: {
        LoadingButton,
        CheckIcon
    }
}
</script>

<style lang="scss" scoped>
@import "@/module/app/theme/color.scss";
@import "@/module/app/theme/dialog.scss";

.message-wrapper {
    display: flex;
    flex-direction: column;
    padding: 12px 20px;
    word-break: break-word;
    .top-send {
        display: flex;
        font-size: 14px;
        line-height: 19px;
        padding: 10px 0;
        align-items: center;
    }
    .input-field {
        display: flex;
        margin-bottom: 8px;
        .el-checkbox {
            margin-top: 10px;
            width: 50px;
        }
        .input-text {
            flex-grow: 1;
        }
        .input-error {
            display: inline-block;
            font-size: 12px;
            color: transparent;
            margin-top: 4px;
            transition: color 0.2s ease-in;
        }
        .input-error.show {
            color: $danger-color;
        }
    }
    .message-content {
        color: $black-color;
        padding: 12px;
        font-size: 14px;
        line-height: 18px;
        background-color: $grey-000;
        border: 1px solid $grey-100;
    }
    .message-success {
        color: $grey-700;
        margin-top: 15px;
    }
    .submit-button {
        @extend %dialog-action;
        margin-top: 40px;
        justify-content: flex-end;
    }
}
</style>

<style scoped>
.message-dialog >>> .el-dialog__body {
	padding: 0px;
}
.message-dialog >>> .el-dialog__header {
	background-color: #2d3e4f;
	padding: 10px 20px 10px;
}
.message-dialog >>> .el-dialog__title {
	color: #ffffff;
	font-size: 14px;
}
.message-dialog >>> .el-dialog__headerbtn {
	top: 12px;
}
.message-dialog >>> .el-dialog__close:hover {
	font-weight: bold;
}
.message-dialog >>> .el-dialog__close {
	color: #ffffff;
	font-size: 20px;
}
.message-dialog >>> .el-dialog__headerbtn:hover .el-dialog__close {
	color: #ffffff;
}
</style>
